import { useCallback, useMemo } from 'react';
import { EDGE_API } from '~/lib/constants';
import { useTenant, useAnalytics } from '@parcel2go/npm-p2g-app';
import { isEmail } from '@parcel2go/core-ui';
import { useAuth } from 'react-oidc-context';
import { authenticatedFetchWithTenantAnalytics } from '~/lib/fetch';
import { Account, CommunicationPreferences } from '~/lib/types/basket-types';
import useBasketSection from './use-basket-section';
import { BasketSections } from '~/lib/types/enums';
import useWorkflowActions from './use-workflow-actions';
import { PublicSettings } from '~/lib/ui-serverless-settings';

const StoreNumberForCommPref = (mobile?: string) => {
  // Check if the mobile number is a UK mobile number
  return !!mobile && mobile.length >= 10 && (mobile.startsWith('07') || mobile.startsWith('+44'));
};

export default function useOptInsPersister() {
  const { user } = useAuth();
  const account = useBasketSection<Account>({ name: BasketSections.ACCOUNT });
  const { tenant, settings } = useTenant();
  const { session } = useAnalytics();
  const { workflowData } = useWorkflowActions();

  const value = useMemo(() => {
    const v = workflowData.get('COMMS');
    if (typeof v === 'object') {
      return v as CommunicationPreferences;
    }
    return {} as CommunicationPreferences;
  }, [workflowData]);

  const persist = useCallback(async () => {
    if (value && Object.keys(value).length > 0 && isEmail(account.data.email || '')) {
      return await authenticatedFetchWithTenantAnalytics(
        user,
        tenant,
        settings,
        session,
        `${EDGE_API}/${tenant}/order-process/optins/store`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            preferences: value,
            mobile:
              settings[PublicSettings.ACCOUNTMOBILE] === 'true' && StoreNumberForCommPref(account.data.mobile)
                ? account.data.mobile
                : undefined,
            email: account.data.email,
            forename: account.data.forename,
            surname: account.data.surname
          })
        }
      ).then((res) => res.json());
    }
  }, [
    value,
    account.data.email,
    account.data.forename,
    account.data.surname,
    account.data.mobile,
    user,
    tenant,
    settings,
    session
  ]);

  return {
    persist
  };
}
